import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { Switch } from '../../../../../components/ui/Switch';
import { createSlot, updateSlot } from '../../../../../features/slots/slotsThunks';
import { ZONE_OPTIONS } from '../../../../../features/slots/slotsConstants';
import {
  buildSlotPayload,
  slotFormFrom,
  zoneHint,
} from '../../../../../features/slots/slotsUtils';

/**
 * Creates a location in a warehouse, or edits one.
 *
 * The zone is not decoration: only "Almacenaje" and "Picking" leave stock
 * sellable, and the form says so under the select before somebody builds a
 * rack in the wrong one.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.warehouse - Where a new location is created.
 * @param {object|null} props.slot - The location being edited; `null` for a new one.
 * @param {(message: string) => void} props.onSaved - Told what happened, in a sentence.
 */
export const SlotFormModal = ({ isOpen, onClose, warehouse, slot, onSaved }) => {
  const dispatch = useDispatch();
  const saving = useSelector((state) => state.slots.saving);

  const [form, setForm] = useState(() => slotFormFrom(slot));
  const [saveError, setSaveError] = useState(null);

  // Seeded on each opening, from the row that asked for it.
  useEffect(() => {
    if (!isOpen) return;
    setForm(slotFormFrom(slot));
    setSaveError(null);
  }, [isOpen, slot]);

  const isEditing = Boolean(slot);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    setSaveError(null);
    const values = buildSlotPayload(form);

    const action = await dispatch(
      isEditing
        ? updateSlot({ slotId: slot.id, values })
        : createSlot({ warehouseId: warehouse.id, values })
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    onSaved(isEditing ? 'Ubicación actualizada.' : `Se creó ${values.name}.`);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Editar ${slot.name}` : 'Nueva ubicación'}
      testId="slot-form-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {isEditing ? slot.warehouse_name : warehouse?.name}
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="slot-form-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        <Field
          label="Ubicación"
          htmlFor="slot-name"
          hint="Es la dirección del lugar: lo que se lee en el rack y se busca en las pantallas."
        >
          <input
            id="slot-name"
            type="text"
            data-testid="slot-name"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
            disabled={saving}
            className={inputClass}
          />
        </Field>

        <Field
          label="Código de barras"
          htmlFor="slot-code"
          hint="Lo que se escanea. Déjalo vacío mientras no haya etiquetas."
        >
          <input
            id="slot-code"
            type="text"
            data-testid="slot-code"
            value={form.code}
            onChange={(event) => set({ code: event.target.value })}
            disabled={saving}
            className={inputClass}
          />
        </Field>

        <div>
          <FilterableSelect
            variant="form"
            testId="slot-zone"
            label="Zona"
            value={form.zone_type}
            onChange={(zone_type) => set({ zone_type })}
            options={ZONE_OPTIONS}
            searchPlaceholder="Buscar zona..."
            disabled={saving}
          />
          <p data-testid="slot-zone-hint" className="text-text-muted text-xs mt-1">
            {zoneHint(form.zone_type)}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Orden de recorrido"
            htmlFor="slot-pick-rank"
            hint="Menor es más cerca de la preparación. Cero es sin orden. Deja huecos (10, 20, 30) para intercalar después."
          >
            <input
              id="slot-pick-rank"
              type="number"
              min="0"
              data-testid="slot-pick-rank"
              value={form.pick_rank}
              onChange={(event) => set({ pick_rank: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>

          <Field
            label="Capacidad en unidades"
            htmlFor="slot-capacity"
            hint="Cero es sin límite. Si se supera, el sistema avisa pero no bloquea: la mercadería ya está ahí."
          >
            <input
              id="slot-capacity"
              type="number"
              min="0"
              data-testid="slot-capacity"
              value={form.capacity}
              onChange={(event) => set({ capacity: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>
        </div>

        <Switch
          checked={form.is_active}
          onChange={(is_active) => set({ is_active })}
          label="Activa"
          ariaLabel="Ubicación activa"
          disabled={saving}
          testId="slot-active"
        />
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Guardando..."
          disabled={!form.name.trim()}
          onClick={handleSave}
          data-testid="slot-save"
        >
          Guardar
        </Button>
      </div>
    </Modal>
  );
};
